import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Fragment,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import Course from './RequirementCourse';
import GeneralStudy from './RequirementGeneralStudy';
import Elective from './RequirementElective';
import Text from './RequirementText';
import { Button } from '@asu/components-core/dist/libCore.es';
import CourseListForm from './CourseListForm';
import Check from './RequirementCheck';
import { encodeCourseHours, decodeCourseHours } from 'creditHours';
// import { useSelector } from 'react-redux';
// import { useDispatch } from 'react-redux';

const MultipleCourseList = forwardRef(
  (
    {
      year,
      dataFromParent,
      closeEditModal,
      newRequirement = false,
      courses,
      setCourses,
    },
    ref
  ) => {
    const [formData, setFormData] = useState([]);
    const formDataRef = useRef(formData);
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [requirementId, setRequirementId] = useState('');
    const [showAsMultiple, setShowAsMultiple] = useState(true);
    const [showAsCourseList, setShowAsCourseList] = useState(false);

    const handleShowAsModal = () => {
      setShowAsMultiple(true);
      setShowAsCourseList(false);
    };

    // handle the user check AND/ Or, open course requirement modal by default
    const handleSelectChange = useCallback(
      (selectedValue, selectedConnector) => {
        if (selectedValue) {
          setSelectedComponents([...selectedComponents, selectedValue]);
          setFormData(
            formData
              ? [
                  ...formData,
                  {
                    data: {
                      itemType: selectedValue,
                      ...(selectedConnector && {
                        connector: selectedConnector,
                      }),
                    },
                  },
                ]
              : [
                  {
                    data: {
                      itemType: selectedValue,
                      ...(selectedConnector && {
                        connector: selectedConnector,
                      }),
                    },
                  },
                ]
          );
        }
      },
      [formData, selectedComponents]
    );

    const handleInputChange = (index, field, value) => {
      setFormData((prevFormData) => {
        const newFormData = [...prevFormData];
        const updatedObject = { ...newFormData[index] };
        const updatedData = { ...updatedObject.data };
        updatedData[field] = value;
        updatedObject.data = updatedData;
        newFormData[index] = updatedObject;
        return newFormData;
      });
    };

    const handleDeleteComponent = (index) => {
      setSelectedComponents(selectedComponents.filter((_, i) => i !== index));
      setFormData(formData.filter((_, i) => i !== index));
    };

    const handleAND = () => {
      handleSelectChange('req_course', 'AND');
    };

    const handleOr = () => {
      handleSelectChange('req_course', 'or');
    };

    const handleSaveCourses = async (returnUpdatedCourses = false) => {
      let updatedCourse = formData.map((item) => item.data);

      // Encode a genuine 0 credit-hour value as the sentinel so it is
      // distinguishable from "no value" (null/empty). null/'' are left
      // untouched to preserve the proposed/non-proposed mutual exclusivity.
      // Covers the course's own fields and any nested recommended courses.
      updatedCourse = updatedCourse.map((course) => {
        if (
          course.itemType === 'req_check' &&
          course.checkSubtype === 'courses'
        ) {
          return {
            ...course,
            checkCourses: course.checkCourses.filter(
              (checkCourse) => checkCourse.subject !== ''
            ),
          };
        }
        return encodeCourseHours(course);
      });
      // check if the first course is empty, if empty, not save the empty requirement, if not, save the requirement
      if (
        updatedCourse &&
        updatedCourse.length > 0 &&
        (updatedCourse[0].subject ||
          updatedCourse[0].gs ||
          updatedCourse[0].customText ||
          updatedCourse[0].group ||
          updatedCourse[0].itemType === 'req_elective')
      ) {
        const newCourseData = {
          ...dataFromParent.details,
          requirementId: requirementId,
          courses: updatedCourse,
          // Inside a course list the requirement-level hours are meaningless;
          // credit hours live on the individual course items. Always null here.
          hours: null,
          maxHours: null,
          itemType: 'req_multiple',
        };

        const next = [...courses];

        if (newRequirement) next.push(newCourseData);
        else
          for (let i = 0; i < next.length; i++)
            if (next[i].requirementId === requirementId)
              next[i] = newCourseData;

        setCourses([...next]);

        // Return the updated courses array if requested
        if (returnUpdatedCourses) {
          return next;
        }
      }

      if (!returnUpdatedCourses) {
        closeEditModal(true);
      }

      return returnUpdatedCourses ? courses : undefined;
    };

    const renderComponent = (component, index) => {
      switch (component) {
        case 'req_course':
          return (
            <div key={`course-${index}`}>
              <Course
                index={index}
                year={year}
                data={formData[index].data}
                handleInputChange={handleInputChange}
                handleDeleteComponent={handleDeleteComponent}
                showCourseListOption={false}
                isCourseListItem={true}
              />
            </div>
          );
        case 'req_gs':
          return (
            <div key={`gs-${index}`}>
              <GeneralStudy
                index={index}
                data={formData[index].data}
                handleInputChange={handleInputChange}
                handleDeleteComponent={handleDeleteComponent}
                showCourseListOption={false}
                isCourseListItem={true}
              />
            </div>
          );
        case 'req_elective':
          return (
            <div key={`elective-${index}`}>
              <Elective
                index={index}
                data={formData[index].data}
                handleInputChange={handleInputChange}
                handleDeleteComponent={handleDeleteComponent}
                showCourseListOption={false}
                isCourseListItem={true}
              />
            </div>
          );
        case 'req_text':
          return (
            <div key={`text-${index}`}>
              <Text
                index={index}
                data={formData[index].data}
                handleInputChange={handleInputChange}
                handleDeleteComponent={handleDeleteComponent}
                showCourseListOption={false}
                isCourseListItem={true}
              />
            </div>
          );
        case 'req_check':
          return (
            <div key={`text-${index}`}>
              <Check
                index={index}
                data={formData[index].data}
                handleInputChange={handleInputChange}
                handleDeleteComponent={handleDeleteComponent}
                showCourseListOption={false}
                isCourseListItem={true}
              />
            </div>
          );
        default:
          return null;
      }
    };

    useEffect(() => {
      formDataRef.current = formData;
    }, [formData]);

    // Handle prop updates directly
    useEffect(() => {
      console.log('dataFromParent:::::: ', dataFromParent);

      const initialData = {
        courses: dataFromParent.details?.courses ?? [],
        requirementId: dataFromParent.details?.requirementId ?? uuidv4(),
      };

      console.log('initialData: ', initialData);

      // Decode the sentinel (0.0001) back to a clean 0 for editing/display, and
      // treat a legacy plain 0 as "unset" (null). Using sentinelOnly here would
      // leave a legacy 0 intact, which the save path would then re-encode to
      // 0.0001 — silently turning an unset value into an explicit zero just by
      // opening and re-saving. zeroAsNull avoids that. Covers the course's own
      // fields and any nested recommended courses.
      const courseData = initialData.courses.map((item) => ({
        data: decodeCourseHours(item, { zeroAsNull: true }),
      }));

      if (
        courseData.length > 0 ||
        (formDataRef.current && formDataRef.current.length > 0)
      ) {
        setFormData(courseData);
        setRequirementId(initialData.requirementId);
      } else {
        setRequirementId(initialData.requirementId);
      }

      console.log('initial form data: ', courseData);

      // for new requirement, the default type is coure requirement
      if (courseData.length === 0) {
        setSelectedComponents(['req_course']);
        setFormData([
          {
            data: {
              itemType: 'req_course',
            },
          },
        ]);
      } else {
        setSelectedComponents(courseData.map((item) => item.data.itemType));
      }
    }, [dataFromParent]);

    // Function you want to expose to the parent (courseListComponent)
    const saveCourses = (message) => {
      console.log('form data:', formData);
      if (formData.length > 0 && formData[0]?.data?.hours >= 0) {
        // check if the first course has hours, if not, doesn't save
        return handleSaveCourses(true); // Request updated courses to be returned
      }
      return Promise.resolve(courses); // Return current courses if nothing to save
    };

    // Use useImperativeHandle to expose childFunction to the parent
    useImperativeHandle(ref, () => ({
      callChildFunction(message) {
        return saveCourses(message);
      },
    }));

    return (
      <>
        {showAsMultiple ? (
          <div className="row py-2 mx-0 border border-top-0">
            <div className="d-flex justify-content-between">
              <div className="ms-4" style={{ width: '85%' }}>
                <div id="componentsContainer">
                  {selectedComponents.map((component, index) =>
                    renderComponent(component, index)
                  )}
                </div>
                <div className="d-flex gap-1 ms-5">
                  <Button
                    label="AND"
                    size="xsmall"
                    classes={[
                      'btn bg-white btn-sm border border-gray-4 text-gray-6',
                    ]}
                    icon={['fas', 'plus']}
                    onClick={handleAND}
                  />

                  <Button
                    label="or"
                    size="xsmall"
                    classes={[
                      'btn bg-white btn-sm border border-gray-4 text-gray-6',
                    ]}
                    icon={['fas', 'plus']}
                    onClick={handleOr}
                  />
                </div>
              </div>

              {/* <div
                className="d-flex gap-1 justify-content-end py-1"
                style={{ width: '15%', height: 'min-content' }}
              >
                <Button
                  color="maroon"
                  size="small"
                  icon={['fas', 'check']}
                  onClick={handleSaveCourses}
                />

                <Button
                  size="small"
                  icon={['fas', 'xmark']}
                  classes={[
                    'btn bg-white btn-md border border-gray-4 text-gray-6',
                  ]}
                  onClick={() => closeEditModal(true)}
                />
              </div> */}
            </div>
          </div>
        ) : showAsCourseList ? (
          <CourseListForm
            // courseListId={courseListId}
            handleClose={handleShowAsModal}
          />
        ) : (
          <div></div>
        )}
      </>
    );
  }
);

export default MultipleCourseList;
